// Debounced POST /Conditions/preview. Whenever the builder's indicator specs change,
// debounces 250ms then re-fetches the flat condition list. UI checkbox components
// subscribe so the long/short pickers refresh as the user tweaks parameters.

import { useEffect, useState } from "react";
import { previewConditions, type ConditionPreviewEntry } from "../api/conditions";
import type { IndicatorSpecDto } from "../api/types";

const DEBOUNCE_MS = 250;

export interface ConditionPreviewState {
  conditions: ConditionPreviewEntry[];
  loading: boolean;
  error: string | null;
}

export function useConditionPreview(
  indicatorSpecs: IndicatorSpecDto[],
  useAdvanced: boolean,
  usePriceAction: boolean,
): ConditionPreviewState {
  const [state, setState] = useState<ConditionPreviewState>({ conditions: [], loading: false, error: null });

  // Re-fetch whenever the inputs change. Stringify for a stable dep that survives
  // referential changes in indicatorSpecs from setState calls.
  const fingerprint = JSON.stringify({ indicatorSpecs, useAdvanced, usePriceAction });

  useEffect(() => {
    if (indicatorSpecs.length === 0) {
      setState({ conditions: [], loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    const ac = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const result = await previewConditions({ indicatorSpecs, useAdvanced, usePriceAction }, ac.signal);
        setState({ conditions: result, loading: false, error: null });
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setState({ conditions: [], loading: false, error: (err as Error).message });
      }
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  return state;
}
